import { type JSX } from "react";
import type { components } from "../../../api/schema";

type WorldlineNode = components["schemas"]["WorldlineNode"];

export type EndingEntry = {
  worldline: WorldlineNode;
  /**
   * その worldline の最終 scene id。未 ended の worldline は null で渡す。
   * 最終 scene が existing でも scenes 未ロード時は null で渡して「未確認」表示にする。
   */
  finalSceneId: string | null;
};

export type PalimpsestEndingColumnProps = {
  /** 描画する筆跡のエンディング情報。画面上と同じ縦順序で渡す (親が並べる)。 */
  entries: EndingEntry[];
};

/**
 * worldline-palimpsest (Phase 3-1, Task 5.1) palimpsest grid の右端 ending 集積列。
 *
 * 各筆跡の最終 scene を 1 マスとして右端に縦に並べる。到達済 (isEnded=true) は金色、
 * 未到達は淡灰色で区別する (Req 4 ending 集積 / Req 6.4 色のみでなく記号も併用)。
 */
export function PalimpsestEndingColumn({ entries }: PalimpsestEndingColumnProps): JSX.Element {
  return (
    <div
      data-testid="palimpsest-ending-column"
      className="flex flex-col gap-1"
      aria-label="各筆跡のエンディング集積"
    >
      {entries.map(({ worldline, finalSceneId }) => {
        const isEnded = worldline.isEnded;
        return (
          <div
            key={worldline.id}
            data-testid={`palimpsest-ending-${worldline.id}`}
            data-ended={isEnded ? "true" : "false"}
            className={[
              "inline-flex items-center justify-center w-6 h-6 rounded-full border text-[10px] font-mono",
              isEnded
                ? "border-amber-400 bg-amber-900/40 text-amber-200"
                : "border-slate-600 bg-slate-800/30 text-slate-500",
            ].join(" ")}
            title={
              isEnded && finalSceneId !== null
                ? `エンディング到達: ${finalSceneId.slice(0, 8)}`
                : "未到達"
            }
          >
            {isEnded ? "★" : "…"}
          </div>
        );
      })}
    </div>
  );
}
